import { ReactNode, useEffect, useState } from 'react'
import clsx from 'clsx'
import { NumberFormatter } from '@mantine/core'
import { Typography } from '../../common/typography'
import classes from './RingProgressCard.module.css'

export default function RingProgressCard({
  title,
  value,
  color,
  size = 320,
  valueLabel,
}: {
  title: string
  value: number
  color?: string
  size?: number
  valueLabel?: ReactNode
}) {
  const strokeWidth = 16
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  const progressPercentage = Math.min(value, 100)
  const targetOffset =
    circumference - (progressPercentage / 100) * circumference

  const [offset, setOffset] = useState(circumference)

  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(targetOffset)
    }, 200)

    return () => clearTimeout(timer)
  }, [targetOffset])

  return (
    <div className={classes.container}>
      <div className={classes.circularProgress}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={classes.progressRing}
        >
          <circle
            className={classes.backgroundCircle}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            className={clsx(classes.progressCircle, {
              [classes.orange]: !color && value > 90 && value < 100,
              [classes.red]: !color && value <= 90,
            })}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={color ? { stroke: color } : undefined}
          />
        </svg>
        <div className={classes.progressContent}>
          <Typography
            fs="xl"
            fc={{ light: 'gray-3', dark: 'gray-3' }}
            ta="center"
            lh="110"
          >
            {title}
          </Typography>
          {valueLabel ? (
            <div className={classes.value}>{valueLabel}</div>
          ) : (
            <NumberFormatter
              className={classes.value}
              decimalScale={1}
              suffix="%"
              value={value}
            />
          )}
        </div>
      </div>
    </div>
  )
}
